import { FaWhatsapp } from "react-icons/fa";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { MessageCircle } from "lucide-react";

import SmoothScroll from "@/components/layout/SmoothScroll";
import Loader from "@/components/layout/Loader";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { COMPANY } from "@/data/content";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Machines from "@/pages/Machines";
import MachineDetail from "@/pages/MachineDetail";
import Brochure from "@/pages/Brochure";
import Clients from "@/pages/Clients";
import Gallery from "@/pages/Gallery";
import Careers from "@/pages/Careers";
import Contact from "@/pages/Contact";
import Enquiry from "@/pages/Enquiry";
import Quote from "@/pages/Quote";
import Faq from "@/pages/Faq";
import Legal from "@/pages/Legal";
import AdminInbox from "@/pages/AdminInbox";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const PageWrap = ({ children }) => (
  <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
    {children}
  </motion.main>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageWrap><Home /></PageWrap>} />
        <Route path="/about" element={<PageWrap><About /></PageWrap>} />
        <Route path="/services" element={<PageWrap><Services /></PageWrap>} />
        <Route path="/machines" element={<PageWrap><Machines /></PageWrap>} />
        <Route path="/machines/:id" element={<PageWrap><MachineDetail /></PageWrap>} />
        <Route path="/machines/:id/brochure" element={<PageWrap><Brochure /></PageWrap>} />
        <Route path="/clients" element={<PageWrap><Clients /></PageWrap>} />
        <Route path="/gallery" element={<PageWrap><Gallery /></PageWrap>} />
        <Route path="/careers" element={<PageWrap><Careers /></PageWrap>} />
        <Route path="/contact" element={<PageWrap><Contact /></PageWrap>} />
        <Route path="/enquiry" element={<PageWrap><Enquiry /></PageWrap>} />
        <Route path="/quote" element={<PageWrap><Quote /></PageWrap>} />
        <Route path="/faq" element={<PageWrap><Faq /></PageWrap>} />
        <Route path="/privacy" element={<PageWrap><Legal type="privacy" /></PageWrap>} />
        <Route path="/terms" element={<PageWrap><Legal type="terms" /></PageWrap>} />
        <Route path="/admin" element={<PageWrap><AdminInbox /></PageWrap>} />
        <Route path="/admin/inbox" element={<PageWrap><AdminInbox /></PageWrap>} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(t);
  }, []);

  return (
    <BrowserRouter>
      <SmoothScroll>
        <AnimatePresence>{loading && <Loader key="loader" />}</AnimatePresence>
        <ScrollToTop />
        <Navbar />
        <AnimatedRoutes />
        <Footer />
        <a
          href={`https://wa.me/${COMPANY.whatsapp}`}
          target="_blank" rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          data-testid="whatsapp-fab" aria-label="WhatsApp"
        >
          <FaWhatsapp className="w-7 h-7 text-white" />
        </a>
        <Toaster position="top-center" theme="light" richColors />
      </SmoothScroll>
    </BrowserRouter>
  );
}

export default App;
